import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton, Button, Grid, TextField } from '@mui/material';
import { Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAddChildrenToLotMutation, useModifySingleLotMutation, useAddLotMutation } from '../../features/api/rest/apiLotsSlice';
import { setAlertMessage } from '../../features/layoutSlice';

function AddLotDialog({ action, ean, opk, id = '', isOpen, setIsOpen, authority = '', code = '', from = null, to = null }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [dialogFullscreen, setDialogFullscreen] = useState(false);
  const [dialogAuthority, setAuthority] = useState(authority);
  const [dialogCode, setCode] = useState(code);
  const [validFrom, setValidFrom] = useState(from);
  const [validTo, setValidTo] = useState(to);
  const [addLot] = useAddChildrenToLotMutation();
  const [modifyLot] = useModifySingleLotMutation();
  const [addRootLot] = useAddLotMutation();
  let title = '';
  let button = '';
  let handleConfirm = () => {};

  const handleAdd = async () => {
    try {
      await addLot({ ean, id, opk, payload: { authority: dialogAuthority, code: dialogCode, info: null, valid_from: validFrom, valid_to: validTo } });
      dispatch(setAlertMessage({ text: t('organizations.product.lots.addLots.successMessage'), type: 'success' }));
    } catch (error) {
      dispatch(setAlertMessage({ text: t('organizations.product.lots.addLots.errorMessage'), type: 'error' }));
    } finally {
      setIsOpen(false);
    }
  };

  const handleEdit = async () => {
    try {
      await modifyLot({ ean, id, opk, payload: { authority: dialogAuthority, code: dialogCode, info: null, valid_from: validFrom, valid_to: validTo } });
      dispatch(setAlertMessage({ text: t('organizations.product.lots.modifyLots.successMessage'), type: 'success' }));
    } catch (error) {
      dispatch(setAlertMessage({ text: t('organizations.product.lots.modifyLots.errorMessage'), type: 'error' }));
    } finally {
      setIsOpen(false);
    }
  };

  const handleAddRoot = async () => {
    try {
      await addRootLot({ ean, opk, payload: { authority: dialogAuthority, code: dialogCode, info: null, valid_from: validFrom, valid_to: validTo } });
      dispatch(setAlertMessage({ text: t('organizations.product.lots.addLots.successMessage'), type: 'success' }));
    } catch (error) {
      dispatch(setAlertMessage({ text: t('organizations.product.lots.addLots.errorMessage'), type: 'error' }));
    } finally {
      setIsOpen(false);
    }
  };

  switch (action) {
    case 'add':
      title = t('organizations.product.lots.addLots.dialog.title');
      button = t('common.actions.add');
      handleConfirm = handleAdd;
      break;
    case 'edit':
      title = t('organizations.product.lots.modifyLots.dialog.title');
      button = t('common.actions.edit');
      handleConfirm = handleEdit;
      break;
    case 'addRoot':
      title = t('organizations.product.lots.addLots.dialog.title');
      button = t('common.actions.add');
      handleConfirm = handleAddRoot;
      break;
    default:
      title = '';
  }

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullWidth fullScreen={dialogFullscreen}>
      <DialogTitle sx={{ textAlign: 'center' }}>
        <Tooltip title={dialogFullscreen ? t('organizations.product.board.exitFullscreen') : t('organizations.product.board.fullscreen')} placement="right">
          <IconButton onClick={() => setDialogFullscreen(!dialogFullscreen)} size="large" sx={{ position: 'absolute', top: 8, left: 8 }}>
            {dialogFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
        {title}
      </DialogTitle>
      <DialogContent>
        <Grid container display="flex" sx={{ placeContent: 'center' }} justifyContent="left" gap={1} p={0} pt={2}>
          <Grid item xs={5}>
            <TextField
              size="small"
              id="Authority"
              name="Authority"
              required
              label={t('organizations.product.lots.addLots.dialog.labels.authority')}
              variant="outlined"
              fullWidth
              value={dialogAuthority}
              placeholder={t('organizations.product.lots.addLots.dialog.labels.authority')}
              onChange={(e) => setAuthority(e.target.value)}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              size="small"
              id="Authority"
              name="Authority"
              required
              label={t('organizations.product.lots.addLots.dialog.labels.code')}
              variant="outlined"
              fullWidth
              value={dialogCode}
              placeholder={t('organizations.product.lots.addLots.dialog.labels.code')}
              onChange={(e) => setCode(e.target.value)}
            />
          </Grid>
          <Grid container display="flex" sx={{ placeContent: 'center', textAlignLast: 'center' }} justifyContent="left" gap={1} p={0} pt={2}>
            <Grid item xs={5}>
              <DateTimePicker
                id="validFrom"
                name="validFrom"
                label={t('organizations.product.lots.addLots.dialog.labels.validFrom')}
                variant="outlined"
                fullWidth
                value={dayjs(validFrom)}
                onChange={(data) => setValidFrom(data ? dayjs(data).format('YYYY-MM-DD') : null)}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: false,
                    inputProps: {
                      'data-testid': 'validFrom',
                      error: 'false',
                      placeholder: t('common.dates.placeholder'),
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={5}>
              <DateTimePicker
                id="validTo"
                name="validTo"
                label={t('organizations.product.lots.addLots.dialog.labels.validTo')}
                variant="outlined"
                fullWidth
                value={dayjs(validTo)}
                onChange={(data) => setValidTo(data ? dayjs(data).format('YYYY-MM-DD') : null)}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: false,
                    inputProps: {
                      'data-testid': 'validTo',
                      placeholder: t('common.dates.placeholder'),
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
        <Grid container display="flex" justifyContent="space-between" gap={1}>
          <Grid item>
            <Button type="submit" variant="outlined" color="secondary" onClick={() => setIsOpen(false)}>
              {t('organizations.form.close')}
            </Button>
          </Grid>
          <Tooltip title={(!dialogAuthority || !dialogCode) && t('organizations.product.lots.addLots.dialog.tooltip')} placement="top">
            <Grid item>
              <Button type="submit" disabled={!dialogAuthority || !dialogCode} variant="contained" color="primary" onClick={handleConfirm}>
                {button}
              </Button>
            </Grid>
          </Tooltip>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

export default AddLotDialog;

AddLotDialog.propTypes = {
  action: PropTypes.string.isRequired,
  ean: PropTypes.string.isRequired,
  opk: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  authority: PropTypes.string,
  code: PropTypes.string,
  from: PropTypes.string,
  to: PropTypes.string,
};
